
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ProductStickyBarProps {
    price: number;
    quantity: number;
    stock: number;
    setQuantity: React.Dispatch<React.SetStateAction<number>>;
    onAdd: () => void;
    isAdded: boolean;
    isProLocked: boolean;
    isOutOfStock: boolean;
    addToast: (msg: string, type: 'error' | 'success') => void;
}

export const ProductStickyBar = ({
    price,
    quantity,
    stock,
    setQuantity,
    onAdd,
    isAdded,
    isProLocked,
    isOutOfStock,
    addToast
}: ProductStickyBarProps) => {
    const navigate = useNavigate();

    return (
        <div className="fixed bottom-0 left-0 right-0 p-4 z-50 pointer-events-none" role="region" aria-label="Product purchase options">
            {/* Glass Effect Bottom Bar */}
            <div className="w-full md:max-w-xl lg:max-w-2xl mx-auto pointer-events-auto bg-white/30 dark:bg-black/30 backdrop-blur-3xl border border-white/20 dark:border-white/5 rounded-[24px] p-3 shadow-[0_8px_30px_rgb(0,0,0,0.15)] flex items-center gap-3 transition-all duration-300">
                {!isProLocked && !isOutOfStock && (
                    <div className="flex items-center bg-white/20 dark:bg-white/5 rounded-xl h-12 px-1 border border-white/10 dark:border-white/5" role="group" aria-label="Quantity selector">
                        <button onClick={() => quantity > 1 && setQuantity(q => q - 1)} disabled={quantity <= 1} className="w-10 h-full flex items-center justify-center text-text-main dark:text-white hover:text-primary active:scale-90 transition-transform disabled:opacity-30 disabled:cursor-not-allowed" aria-label={`Decrease quantity, currently ${quantity}`}>
                            <span className="material-symbols-outlined text-lg">remove</span>
                        </button>
                        <span className="w-6 text-center font-bold text-text-main dark:text-white" aria-label={`Current quantity: ${quantity}`}>{quantity}</span>
                        <button onClick={() => quantity < stock ? setQuantity(q => q + 1) : addToast(`Máximo ${stock} unidades`, 'error')} disabled={quantity >= stock} className={`w-10 h-full flex items-center justify-center text-text-main dark:text-white active:scale-90 transition-transform disabled:opacity-30 disabled:cursor-not-allowed ${quantity >= stock ? 'opacity-30 cursor-not-allowed' : 'hover:text-primary'}`} aria-label={`Increase quantity, maximum ${stock} units`}>
                            <span className="material-symbols-outlined text-lg">add</span>
                        </button>
                    </div>
                )}
                {isProLocked ? (
                    <button onClick={() => navigate('/login')} className="flex-1 h-12 rounded-xl text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg bg-gradient-to-r from-primary to-purple-600 hover:opacity-90 active:scale-[0.98] transition-all" aria-label="Login to purchase this exclusive Club product">
                        <span className="material-symbols-outlined text-lg">lock</span>
                        <span>Unirse para Comprar</span>
                    </button>
                ) : isOutOfStock ? (
                    <button disabled className="flex-1 h-12 rounded-xl text-text-secondary bg-gray-200 dark:bg-white/10 font-bold text-base flex items-center justify-center gap-2 cursor-not-allowed" aria-label="Product out of stock">
                        <span>Sin Stock</span>
                    </button>
                ) : (
                    <button onClick={onAdd} className={`flex-1 h-12 transition-all rounded-xl text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg ${isAdded ? 'bg-green-500 shadow-green-500/30' : 'bg-primary hover:bg-orange-700 active:scale-95 shadow-orange-500/20'}`} aria-label={isAdded ? 'Product added to cart' : `Add to cart - $${((price || 0) * (quantity || 1)).toLocaleString('es-AR')}`}>
                        {isAdded ? (
                            <>
                                <span>¡Agregado!</span>
                                <span className="material-symbols-outlined text-lg">check</span>
                            </>
                        ) : (
                            <>
                                <span>Agregar</span>
                                <span className="bg-black/20 dark:bg-white/20 rounded px-1.5 py-0.5 text-xs font-medium" aria-label={`Price: $${((price || 0) * (quantity || 1)).toLocaleString('es-AR')}`}>$ {((price || 0) * (quantity || 1)).toLocaleString('es-AR')}</span>
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};
